import { productStore } from './productStore';
import { chapterStoreKey, inVersion, seniorityKey } from '../productMock';
import { COST_COUNTRIES, costMatrixStorageKey } from './costs';
import { serviceHoursStorageKey } from './serviceLevels';
import { retentionKey } from './retention';
import { pricingKey, readPricing, writePricing } from './pricing';
import { versionTeamKey } from './versionTeam';

/** Contiguous by construction, so nothing here validates a gap; a number never moves, since values are stored by it. */
export interface ProductVersion {
  /** `2026.1`. Also the storage suffix for everything the version holds. */
  number: string;
  /** Inclusive, `YYYY-MM-DD`. */
  startDate: string;
  /** Inclusive, `YYYY-MM-DD`. The last version of a year ends on its 31 December. */
  endDate: string;
  /** Empty means the name read off its number, which is what almost every version is called. */
  name: string;
}

/** The short form, since the year is already written out in the span beside it. */
export const nameOf = (version: ProductVersion): string => version.name || version.number.slice(2);

/** Another version is already called this, so the name would name two things. */
export const nameTaken = (versions: ProductVersion[], number: string, name: string): boolean => {
  const wanted = name.trim();
  if (!wanted) return false;
  return versions.some(version => version.number !== number && nameOf(version) === wanted);
};

const DAY = /^\d{4}-\d{2}-\d{2}$/;

// UTC arithmetic: these are calendar days, so a local offset must not shift one into its neighbour.
const shiftDay = (day: string, days: number): string =>
  new Date(Date.UTC(Number(day.slice(0, 4)), Number(day.slice(5, 7)) - 1, Number(day.slice(8, 10)) + days))
    .toISOString()
    .slice(0, 10);

/** Checked by writing it back out: `Date.parse` reads 30 February happily, as 2 March. */
export const isDay = (value: string): boolean => DAY.test(value) && shiftDay(value, 0) === value;

export const dayBefore = (day: string): string => shiftDay(day, -1);
export const yearOf = (day: string): string => day.slice(0, 4);
export const startOfYear = (year: string): string => `${year}-01-01`;
export const endOfYear = (year: string): string => `${year}-12-31`;

export const versionsStorageKey = (productId: string): string => `productVersions:${productId}`;

const asVersion = (raw: unknown): ProductVersion | null => {
  if (!raw || typeof raw !== 'object') return null;
  const stored = raw as Record<string, unknown>;
  const { number, startDate, endDate, name } = stored;
  if (typeof number !== 'string' || !number) return null;
  if (typeof startDate !== 'string' || !isDay(startDate)) return null;
  if (typeof endDate !== 'string' || !isDay(endDate) || endDate < startDate) return null;
  return { number, startDate, endDate, name: typeof name === 'string' ? name : '' };
};

/** Oldest first, which is the order every operation here reads the chain in. */
const inOrder = (versions: ProductVersion[]): ProductVersion[] =>
  [...versions].sort((a, b) => a.startDate.localeCompare(b.startDate));

// A value that does not parse reads as no versions.
export const readVersions = (productId: string): ProductVersion[] => {
  try {
    const raw = productStore.getItem(versionsStorageKey(productId));
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    if (!Array.isArray(parsed)) return [];
    return inOrder(parsed.map(asVersion).filter((version): version is ProductVersion => version !== null));
  } catch {
    return [];
  }
};

export const writeVersions = (productId: string, versions: ProductVersion[]): void => {
  productStore.setItem(versionsStorageKey(productId), JSON.stringify(inOrder(versions)));
};

/** Which version a day falls in, or null when it falls outside them all. */
export const versionCovering = (versions: ProductVersion[], day: string): ProductVersion | null => {
  // The calendar day, whatever the caller had: an instant sorts after the day it falls on.
  const on = day.slice(0, 10);
  return versions.find(version => version.startDate <= on && on <= version.endDate) ?? null;
};

/** The nearest version where none covers the date: the rates we have beat no rate at all. */
export const versionFor = (versions: ProductVersion[], day: string): ProductVersion | null => {
  const covering = versionCovering(versions, day);
  if (covering) return covering;
  if (!versions.length) return null;
  return day.slice(0, 10) < versions[0].startDate ? versions[0] : versions[versions.length - 1];
};

/** The version being worked in: the one covering today, or the newest if today is past them all. */
export const currentVersion = (versions: ProductVersion[], today: string): ProductVersion | null =>
  versionCovering(versions, today) ?? versions[versions.length - 1] ?? null;

const numberIn = (version: ProductVersion): number => Number(version.number.split('.')[1]) || 0;

/** A number freed by a delete comes round again, which is why a delete clears its values first. */
export const nextNumber = (versions: ProductVersion[], year: string): string => {
  const used = versions.filter(version => yearOf(version.startDate) === year).map(numberIn);
  return `${year}.${Math.max(0, ...used) + 1}`;
};

/** What is wrong with a start date, as a reason the dialog can name. Null when the split is allowed. */
export type SplitProblem = 'noDay' | 'notLater' | 'pastEnd';

/** Always splits the last one: splitting an earlier span would renumber everything after it. */
export const splitProblem = (versions: ProductVersion[], startDate: string): SplitProblem | null => {
  const last = versions[versions.length - 1];
  if (!last || !isDay(startDate)) return 'noDay';
  // A version has to have a day in it, so the new one starts after the old one began, not on it.
  if (startDate <= last.startDate) return 'notLater';
  if (startDate > last.endDate) return 'pastEnd';
  return null;
};

/** Returns the chain unchanged where the date is not allowed. */
export const splitAt = (
  versions: ProductVersion[],
  startDate: string,
  name = '',
): { versions: ProductVersion[]; created: ProductVersion | null; shortened: ProductVersion | null } => {
  if (splitProblem(versions, startDate)) return { versions, created: null, shortened: null };
  const last = versions[versions.length - 1];
  const created: ProductVersion = {
    number: nextNumber(versions, yearOf(startDate)),
    startDate,
    endDate: last.endDate,
    name,
  };
  const shortened: ProductVersion = { ...last, endDate: dayBefore(startDate) };
  return { versions: [...versions.slice(0, -1), shortened, created], created, shortened };
};

/** What is wrong with a boundary move. Null when it is allowed. */
export type MoveProblem = 'noDay' | 'unknown' | 'first' | 'notLater' | 'pastEnd' | 'otherYear';

/** A start is a shared boundary, so moving it moves the previous version's end with it. */
export const moveProblem = (versions: ProductVersion[], number: string, startDate: string): MoveProblem | null => {
  const index = versions.findIndex(version => version.number === number);
  if (index < 0) return 'unknown';
  if (index === 0) return 'first';
  if (!isDay(startDate)) return 'noDay';
  if (startDate <= versions[index - 1].startDate) return 'notLater';
  if (startDate > versions[index].endDate) return 'pastEnd';
  // Numbers never move, so neither a version's start nor the boundary before it may leave its year.
  if (yearOf(startDate) !== yearOf(versions[index].startDate)) return 'otherYear';
  if (yearOf(dayBefore(startDate)) !== yearOf(versions[index - 1].startDate)) return 'otherYear';
  return null;
};

/** Opens its year, so 1 January is the only start keeping it and the day before inside their own. */
export const opensYear = (versions: ProductVersion[], number: string): boolean => {
  const index = versions.findIndex(version => version.number === number);
  if (index <= 0) return false;
  return yearOf(versions[index - 1].startDate) !== yearOf(versions[index].startDate);
};

export const moveStart = (versions: ProductVersion[], number: string, startDate: string): ProductVersion[] => {
  if (moveProblem(versions, number, startDate)) return versions;
  const index = versions.findIndex(version => version.number === number);
  return versions.map((version, at) => {
    if (at === index - 1) return { ...version, endDate: dayBefore(startDate) };
    if (at === index) return { ...version, startDate };
    return version;
  });
};

export const setName = (versions: ProductVersion[], number: string, name: string): ProductVersion[] =>
  versions.map(version => (version.number === number ? { ...version, name } : version));

/** A year must keep a version, so its last one cannot be deleted. Null when the delete is allowed. */
export type RemoveProblem = 'unknown' | 'onlyOneInYear';

export const removeProblem = (versions: ProductVersion[], number: string): RemoveProblem | null => {
  const version = versions.find(each => each.number === number);
  if (!version) return 'unknown';
  const year = yearOf(version.startDate);
  if (versions.filter(each => yearOf(each.startDate) === year).length < 2) return 'onlyOneInYear';
  return null;
};

/** The same rule `removeVersion` applies, so the dialog can name it first. */
export const absorberOf = (versions: ProductVersion[], number: string): ProductVersion | null => {
  const index = versions.findIndex(version => version.number === number);
  if (index < 0) return null;
  const previous = versions[index - 1];
  return previous && yearOf(previous.startDate) === yearOf(versions[index].startDate)
    ? previous
    : (versions[index + 1] ?? null);
};

/** Its own values are the caller's to clear, via `clearVersionValues`. */
export const removeVersion = (versions: ProductVersion[], number: string): ProductVersion[] => {
  if (removeProblem(versions, number)) return versions;
  const index = versions.findIndex(version => version.number === number);
  const removed = versions[index];
  const previous = versions[index - 1];
  // The one before takes the span back, unless the deleted version opened the year.
  const absorbAt = previous && yearOf(previous.startDate) === yearOf(removed.startDate) ? index - 1 : index + 1;
  return versions
    .map((version, at) => {
      if (at !== absorbAt) return version;
      return absorbAt < index ? { ...version, endDate: removed.endDate } : { ...version, startDate: removed.startDate };
    })
    .filter((_, at) => at !== index);
};

/** The newest terms carry into a fresh `<year>.1`, so nobody is asked every January. */
export const openYear = (
  versions: ProductVersion[],
  today: string,
): {
  versions: ProductVersion[];
  created: ProductVersion | null;
  opened: ProductVersion[];
  from: ProductVersion | null;
} => {
  const year = yearOf(today);
  const last = versions[versions.length - 1];
  if (last && yearOf(last.startDate) >= year) return { versions, created: null, opened: [], from: null };

  // Every year since the last one covered, not only this one: the chain is contiguous by construction.
  const first = last ? Number(yearOf(last.startDate)) + 1 : Number(year);
  const opened: ProductVersion[] = [];
  for (let each = first; each <= Number(year); each += 1) {
    const number = String(each);
    opened.push({ number: `${number}.1`, startDate: startOfYear(number), endDate: endOfYear(number), name: '' });
  }

  return { versions: [...versions, ...opened], created: opened[opened.length - 1] ?? null, opened, from: last ?? null };
};

/** The stored chain, or the one a first visit would open, numbered so an invented year reads the last saved rates. */
export const versionsFor = (productId: string, today: string): ProductVersion[] => {
  const { versions, opened, from } = openYear(readVersions(productId), today);
  if (!from || !opened.length) return versions;
  // An invented year holds no values of its own, so it carries the last stored number to read its rates.
  const invented = new Set(opened.map(version => version.number));
  return versions.map(version => (invented.has(version.number) ? { ...version, number: from.number } : version));
};

/** What "a version" means in storage: a new one copies these, and a delete takes exactly these. */
const versionedKeys = (productId: string): string[] => [
  seniorityKey(productId),
  chapterStoreKey(productId),
  versionTeamKey(productId),
  ...COST_COUNTRIES.map(country => costMatrixStorageKey(productId, country)),
  serviceHoursStorageKey(productId),
  retentionKey(productId),
  pricingKey(productId),
];

/** Copied from the unversioned key where the source was never saved: that is what it reads. */
export const copyVersionValues = (productId: string, from: string, to: string): void => {
  // A hand-added team is planned for its own year, so a new year starts without it.
  const sameYear = from.split('.')[0] === to.split('.')[0];
  versionedKeys(productId)
    .filter(key => sameYear || key !== versionTeamKey(productId))
    .forEach(key => {
      const raw = productStore.getItem(inVersion(key, from)) ?? productStore.getItem(key);
      if (raw !== null) productStore.setItem(inVersion(key, to), raw);
    });
  // A fixed total is agreed once, for its own version, so the next one keeps the mode but not the amount.
  if (readPricing(productId, from).mode === 'fixedPrice') writePricing(productId, to, { mode: 'fixedPrice' });
};

export const clearVersionValues = (productId: string, version: string): void => {
  versionedKeys(productId).forEach(key => productStore.removeItem(inVersion(key, version)));
};

// Read back in UTC: west of Greenwich a local reading of `2026-01-01` is New Year's Eve.
const asDate = (day: string): Date => new Date(`${day}T00:00:00Z`);

/** `1 Jul 2026`, for the span under a version's number. Empty for anything that is not a day. */
export const formatShortDay = (day: string, locale = 'en-IE'): string =>
  isDay(day)
    ? asDate(day).toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' })
    : '';

/** No year: the grid says it once at the top, and a cell is forty pixels wide. */
export const formatDayAndMonth = (day: string, locale = 'en-IE'): string =>
  isDay(day) ? asDate(day).toLocaleDateString(locale, { day: 'numeric', month: 'short', timeZone: 'UTC' }) : '';

/** `30 June 2026`, for the sentence saying what creating a version does to the one before it. */
export const formatDay = (day: string, locale = 'en-IE'): string =>
  isDay(day)
    ? asDate(day).toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' })
    : '';
